import React, { useEffect, useMemo, useRef, useState } from 'react';
import { InspectionItem } from '../../types';
import {
    applyCompanyMerge,
    collectCompanyUsage,
    foldCompanyName,
    summarizeMerge,
    suggestCompanyMerges,
    CompanyMergeGroup,
    CompanyUsage,
} from '../../services/companyMergeLogic';
import { Button } from '../Button';
import { AlertTriangle, Building2, GitMerge, Lock, Plus, Search, Sparkles, Trash2, X } from 'lucide-react';

/**
 * Firmen zusammenfuehren.
 *
 * Der Freitext in `responsiblePerson` fuehrt dazu, dass dieselbe Firma in der
 * Maengeluebersicht mehrfach steht. Dieser Assistent bietet zwei gleichwertige
 * Wege dorthin:
 *
 *  - Vorschlaege: was die Aehnlichkeitssuche selbst gefunden hat,
 *  - Selbst zusammenfuehren: freie Auswahl aus ALLEN Firmen, denn die Automatik
 *    laesst bewusst Faelle liegen ("Meier & Söhne" / "Meier und Söhne").
 *
 * Ohne Vorschlaege ist der Assistent deshalb kein Leerzustand, sondern startet
 * direkt in der manuellen Ansicht.
 *
 * Zusammenfuehren ist nicht umkehrbar: die alte Schreibweise ist danach weg.
 * Darum zeigt die Fusszeile erst die Vorschau, und der Nutzer bestaetigt
 * ausdruecklich — nichts wird beim Anklicken sofort vollzogen.
 *
 * Die manuelle Auswahl zaehlt SOFORT als Plan, sobald ein Ziel feststeht: vier
 * Firmen anhaken, Ziel waehlen, bestaetigen, fertig. „Vormerken" ist nur noch
 * fuer den Fall da, dass man danach eine WEITERE Zusammenfuehrung
 * zusammenstellen will. Frueher lief die Vorschau nur ueber vorgemerkte
 * Eintraege; der Vormerk-Knopf lag unter der Firmenliste ausserhalb des
 * Sichtbereichs, und die Fusszeile meldete bei vier angehakten Firmen „Noch
 * nichts ausgewaehlt" — der Assistent wirkte kaputt.
 *
 * GRUNDREGEL DER OBERFLAECHE: eine Firma ist zu jedem Zeitpunkt hoechstens
 * EINMAL verplant, und der angezeigte Weg ist der, der auch ausgefuehrt wird.
 * Frueher konnte dieselbe Schreibweise in einem Vorschlag UND von Hand verplant
 * sein; die manuelle Zuordnung ueberschrieb die automatische still, waehrend die
 * Vorschlagskarte weiter das alte Ziel zeigte. Statt den Konflikt still
 * aufzuloesen, wird er verhindert: die Handarbeit hat Vorrang, und was von ihr
 * belegt ist, sperrt sichtbar die Gegenseite.
 */
interface Props {
    items: InspectionItem[];
    /** Firmen aus dem Projektverteiler — die „offizielle" Schreibweise als Vorlage. */
    companySuggestions: string[];
    onClose: () => void;
    /**
     * Bestaetigt: bereits umgeschriebene Maengel, Anzahl der Aenderungen und die
     * Ziel-Schreibweisen, auf die zusammengefuehrt wurde (fuer die Rueckmeldung —
     * „umgestellt" allein sagt dem Nutzer nicht, WORAUF).
     */
    onApply: (items: InspectionItem[], changed: number, targets: string[]) => void;
}

type Tab = 'suggestions' | 'manual';
type TargetMode = 'variant' | 'custom';

/** Was der Nutzer an einem Vorschlag geaendert hat. */
interface GroupChoice {
    /** Gruppe wird ausgefuehrt. */
    enabled: boolean;
    /** Varianten, die wirklich dazugehoeren — einzelne lassen sich ausklammern. */
    selected: string[];
    targetMode: TargetMode;
    /** Ziel aus den Varianten der Gruppe (ROHER String). */
    targetVariant: string;
    /** Ziel als freie Eingabe, falls die richtige Schreibweise noch fehlt. */
    customTarget: string;
}

/** Eine vom Nutzer selbst zusammengestellte Zusammenfuehrung. */
interface ManualMerge {
    id: string;
    /** ROHE Quell-Strings. */
    sources: string[];
    target: string;
}

/** Was ein Vorschlag nach Abzug der Handarbeit noch bewirkt. */
interface GroupPlan {
    /** Schreibweisen, die diese Gruppe wirklich umbenennt. */
    sources: string[];
    target: string;
    /** Das Ziel wird selbst von Hand umbenannt — die Gruppe ruht. */
    targetBlocked: boolean;
    /** Quellen, die wegen einer manuellen Zuordnung ausgeklammert sind. */
    blockedSources: string[];
}

const DATALIST_ID = 'company-merge-target-options';
const TITLE_ID = 'company-merge-title';

/**
 * Warnhinweise zu einer Gruppe (z. B. „unterschiedliche Rechtsformen").
 *
 * Defensiv gelesen: das Feld stammt aus companyMergeLogic und fehlt in
 * aelteren Staenden — dann gibt es eben keine Hinweise, statt eines Absturzes.
 */
const groupWarnings = (group: CompanyMergeGroup): string[] => {
    const raw = (group as CompanyMergeGroup & { warnings?: unknown }).warnings;
    if (!Array.isArray(raw)) return [];
    return raw.filter((w): w is string => typeof w === 'string' && !!w.trim());
};

const initialChoices = (groups: CompanyMergeGroup[]): Record<string, GroupChoice> => {
    const out: Record<string, GroupChoice> = {};
    for (const group of groups) {
        out[group.id] = {
            // 'wahrscheinlich' muss der Nutzer bewusst bestaetigen: dort kann die
            // Aehnlichkeitssuche zwei verschiedene Firmen erwischt haben. Eine
            // Gruppe mit Warnung ist NIE vorgehakt — auch bei 'sicher' nicht:
            // die Warnung sagt gerade, dass die Normalisierung hier zu viel
            // weggestrichen hat.
            enabled: group.confidence === 'sicher' && groupWarnings(group).length === 0,
            selected: group.variants.map((v) => v.name),
            targetMode: 'variant',
            targetVariant: group.suggestedTarget,
            customTarget: '',
        };
    }
    return out;
};

const effectiveTarget = (choice: GroupChoice): string =>
    choice.targetMode === 'custom' ? choice.customTarget.trim() : choice.targetVariant;

/** Eine Zusammenfuehrung wirkt nur, wenn mindestens eine Quelle vom Ziel abweicht. */
const hasEffect = (sources: string[], target: string): boolean =>
    !!target.trim() && sources.some((s) => s !== target);

const usageLabel = (usage: CompanyUsage): string => {
    const maengel = `${usage.total} ${usage.total === 1 ? 'Mangel' : 'Mängel'}`;
    return usage.open > 0 ? `${maengel} (${usage.open} offen)` : maengel;
};

/**
 * Alphabetisch ueber die GEFALTETE Schreibweise: so stehen „Mueller Bau" und
 * „Müller Bau GmbH" nebeneinander statt weit auseinander. Nach Haeufigkeit
 * sortiert lagen genau die Varianten, die man vergleichen will, quer verstreut.
 */
const compareFolded = (a: CompanyUsage, b: CompanyUsage): number => {
    const fa = foldCompanyName(a.name);
    const fb = foldCompanyName(b.name);
    if (fa !== fb) return fa < fb ? -1 : 1;
    return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
};

export const CompanyMergeAssistant: React.FC<Props> = ({ items, companySuggestions, onClose, onApply }) => {
    // "Nicht zugewiesen" (leerer Firmenname) kommt hier gar nicht erst an:
    // collectCompanyUsage laesst leere Werte weg.
    const usages = useMemo(() => collectCompanyUsage(items), [items]);
    const groups = useMemo(() => suggestCompanyMerges(usages), [usages]);

    const [tab, setTab] = useState<Tab>(() => (groups.length > 0 ? 'suggestions' : 'manual'));
    const [choices, setChoices] = useState<Record<string, GroupChoice>>(() => initialChoices(groups));
    const [understood, setUnderstood] = useState(false);
    /** Nach dem ersten Klick gesperrt — ein zweiter Aufruf waere sinnlos. */
    const [busy, setBusy] = useState(false);

    // Manuelle Ansicht: Entwurf oben, gesammelte Zusammenfuehrungen darunter.
    const [manualMerges, setManualMerges] = useState<ManualMerge[]>([]);
    const [query, setQuery] = useState('');
    const [draftSources, setDraftSources] = useState<string[]>([]);
    const [draftMode, setDraftMode] = useState<TargetMode>('variant');
    const [draftVariant, setDraftVariant] = useState('');
    const [draftCustom, setDraftCustom] = useState('');

    // Aendern sich die zugrundeliegenden Firmen (die Mangelliste wird im
    // Hintergrund bearbeitet), zeigt die gesammelte Auswahl auf Strings, die es
    // nicht mehr gibt: Vorschlaege wuerden neu berechnet, Haken und vorgemerkte
    // Zusammenfuehrungen blieben vom Mount stehen. Bewusst KEIN Nachziehen im
    // Einzelnen — eine Auswahl, die etwas anderes tut als sie anzeigt, ist
    // schlimmer als eine verworfene Auswahl.
    const usageKey = useMemo(() => usages.map((u) => u.name).join('\u0000'), [usages]);
    const [knownUsageKey, setKnownUsageKey] = useState(usageKey);
    if (knownUsageKey !== usageKey) {
        setKnownUsageKey(usageKey);
        setChoices(initialChoices(groups));
        setManualMerges([]);
        setDraftSources([]);
        setDraftVariant('');
        setDraftCustom('');
        setDraftMode('variant');
        setQuery('');
        setUnderstood(false);
        setBusy(false);
    }

    const updateChoice = (id: string, patch: Partial<GroupChoice>) =>
        setChoices((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

    const toggleVariant = (group: CompanyMergeGroup, name: string) => {
        const choice = choices[group.id];
        if (!choice) return;
        const isSelected = choice.selected.includes(name);
        const selected = isSelected
            ? choice.selected.filter((n) => n !== name)
            : group.variants.map((v) => v.name).filter((n) => n === name || choice.selected.includes(n));
        // Das Ziel muss Teil der Gruppe bleiben: faellt es weg, rueckt die erste
        // verbliebene Variante nach.
        const targetVariant = selected.includes(choice.targetVariant) ? choice.targetVariant : selected[0] || '';
        updateChoice(group.id, { selected, targetVariant });
    };

    const draftTarget = draftMode === 'custom' ? draftCustom.trim() : draftVariant;

    // ─── Wer ist von Hand verplant? ──────────────────────────────────────────
    // Der noch nicht vorgemerkte Entwurf zaehlt mit: sobald eine Firma hier
    // angehakt ist, darf kein Vorschlag sie mehr anfassen — sonst haette der
    // Nutzer zwei widersprechende Ziele vor sich.

    /** Namen, die von Hand UMBENANNT werden. */
    const manualSourceNames = useMemo(() => {
        const set = new Set<string>();
        for (const merge of manualMerges) {
            for (const source of merge.sources) if (source !== merge.target) set.add(source);
        }
        for (const source of draftSources) if (source !== draftTarget) set.add(source);
        return set;
    }, [manualMerges, draftSources, draftTarget]);

    /** Namen, auf die von Hand zusammengefuehrt wird — die muessen stehen bleiben. */
    const manualTargetNames = useMemo(() => {
        const set = new Set<string>();
        for (const merge of manualMerges) if (merge.target.trim()) set.add(merge.target);
        if (draftTarget.trim()) set.add(draftTarget);
        return set;
    }, [manualMerges, draftTarget]);

    /** Quellen, die bereits in einer VORGEMERKTEN Zusammenfuehrung stehen. */
    const bookedManualSources = useMemo(() => {
        const map = new Map<string, string>();
        for (const merge of manualMerges) {
            for (const source of merge.sources) if (source !== merge.target) map.set(source, merge.target);
        }
        return map;
    }, [manualMerges]);

    /**
     * Ziele VORGEMERKTER Zusammenfuehrungen. Die muessen stehen bleiben: wuerde
     * so ein Ziel als Quelle eines neuen Entwurfs umbenannt, entstuende eine
     * Kette (A→B, B→C) — die vorgemerkte Karte zeigte „→ B", angewendet wuerde
     * aber „→ C".
     */
    const bookedManualTargets = useMemo(() => {
        const set = new Set<string>();
        for (const merge of manualMerges) if (merge.target.trim()) set.add(merge.target);
        return set;
    }, [manualMerges]);

    /**
     * Was jeder angehakte Vorschlag nach Abzug der Handarbeit noch bewirkt.
     * Handarbeit gewinnt, deshalb haengt diese Rechnung an den manuellen Namen —
     * und nie umgekehrt.
     */
    const groupPlans = useMemo(() => {
        const out = new Map<string, GroupPlan>();
        for (const group of groups) {
            const choice = choices[group.id];
            if (!choice || !choice.enabled) continue;
            const target = effectiveTarget(choice);
            const blockedSources = choice.selected.filter(
                (s) => s !== target && (manualSourceNames.has(s) || manualTargetNames.has(s)),
            );
            // Ein Ziel, das selbst von Hand umbenannt wird, waere eine Kette: die
            // Karte verspraeche ein Ziel, das es hinterher nicht mehr gibt.
            const targetBlocked = !!target.trim() && manualSourceNames.has(target);
            const sources =
                targetBlocked || !target.trim()
                    ? []
                    : choice.selected.filter((s) => s !== target && !blockedSources.includes(s));
            out.set(group.id, { sources, target, targetBlocked, blockedSources });
        }
        return out;
    }, [groups, choices, manualSourceNames, manualTargetNames]);

    /** Name -> Ziel des Vorschlags, der ihn belegt (Ziele zeigen auf sich selbst). */
    const plannedBySuggestion = useMemo(() => {
        const map = new Map<string, string>();
        for (const plan of groupPlans.values()) {
            if (plan.sources.length === 0) continue;
            for (const source of plan.sources) map.set(source, plan.target);
            map.set(plan.target, plan.target);
        }
        return map;
    }, [groupPlans]);

    /**
     * Ist diese Schreibweise einer Vorschlagsgruppe von Hand belegt? Deckt sich
     * mit `blockedSources` in groupPlans: das Ziel selbst ist nicht gesperrt,
     * solange es nicht seinerseits umbenannt wird (dafuer gibt es targetBlocked).
     */
    const isLockedByHand = (name: string, target: string): boolean =>
        manualSourceNames.has(name) || (name !== target && manualTargetNames.has(name));

    /** Warum eine Firma in der manuellen Liste gesperrt ist — oder null. */
    const manualLockReason = (name: string): string | null => {
        const booked = bookedManualSources.get(name);
        if (booked !== undefined) return `bereits vorgemerkt → ${booked}`;
        if (bookedManualTargets.has(name)) return 'Ziel einer vorgemerkten Zusammenführung';
        const planned = plannedBySuggestion.get(name);
        if (planned === undefined) return null;
        return planned === name ? 'Ziel eines Vorschlags' : `bereits in einem Vorschlag verplant → ${planned}`;
    };

    const toggleDraftSource = (name: string) => {
        if (manualLockReason(name)) return;
        const next = draftSources.includes(name)
            ? draftSources.filter((n) => n !== name)
            : [...draftSources, name];
        setDraftSources(next);
        // Das Ziel muss eine der ausgewaehlten Firmen bleiben.
        if (!next.includes(draftVariant)) setDraftVariant(next[0] || '');
    };

    /**
     * Suche und Sortierung laufen ueber dieselbe Faltung wie der Vergleich:
     * „müller" muss „Mueller Bau" finden — das ist der haeufigste Grund
     * ueberhaupt, warum es zwei Schreibweisen gibt.
     */
    const visibleUsages = useMemo(() => {
        const needle = foldCompanyName(query);
        return usages
            .filter((u) => !needle || foldCompanyName(u.name).includes(needle))
            .slice()
            .sort(compareFolded);
    }, [usages, query]);

    /**
     * Ein frei getipptes Entwurfsziel, das selbst schon vorgemerkt umbenannt
     * wird: „C → A" stuende da, angewendet wuerde ueber die Kette „C → B". Der
     * Entwurf gilt dann nicht — und sagt, worauf die Kette tatsaechlich fuehrt.
     * Ueber die Varianten-Radios kann das nicht passieren (vorgemerkte Quellen
     * sind in der Liste gesperrt), nur ueber das Freitextfeld.
     */
    const draftTargetRenamedTo = bookedManualSources.get(draftTarget);
    const draftConflictHint =
        draftTargetRenamedTo === undefined
            ? null
            : `Die Ziel-Schreibweise „${draftTarget}" wird bereits zu „${draftTargetRenamedTo}" umbenannt. Wählen Sie „${draftTargetRenamedTo}" als Ziel oder entfernen Sie die vorgemerkte Zusammenführung.`;
    const canAddDraft =
        draftSources.length > 0 && hasEffect(draftSources, draftTarget) && draftTargetRenamedTo === undefined;

    const addDraft = () => {
        if (!canAddDraft) return;
        setManualMerges((prev) => [
            ...prev,
            { id: `manual-${Date.now()}-${prev.length}`, sources: draftSources, target: draftTarget },
        ]);
        setDraftSources([]);
        setDraftVariant('');
        setDraftCustom('');
        setDraftMode('variant');
        setQuery('');
    };

    /**
     * Schreibweisen, die es bisher weder in den Maengeln noch im Verteiler gibt.
     * Ein Tippfehler im Freitextfeld legt sonst kommentarlos eine dritte,
     * nagelneue Schreibweise an — blockiert wird das nicht, nur sichtbar gemacht.
     */
    const knownNames = useMemo(() => {
        const set = new Set<string>();
        for (const u of usages) set.add(u.name.trim());
        for (const c of companySuggestions) if (c && c.trim()) set.add(c.trim());
        return set;
    }, [usages, companySuggestions]);

    const isNewSpelling = (value: string): boolean => !!value.trim() && !knownNames.has(value.trim());

    // Beide Wege muenden in EINE Zuordnung: eine Vorschau, eine Bestaetigung.
    const mapping = useMemo(() => {
        const out: Record<string, string> = {};
        for (const plan of groupPlans.values()) {
            for (const source of plan.sources) out[source] = plan.target;
        }
        // Manuelles gewinnt: es ist die ausdrueckliche Entscheidung des Nutzers.
        // Ueberschreiben kann es hier nichts mehr — die Vorschlaege haben die von
        // Hand belegten Namen oben schon ausgeklammert.
        for (const merge of manualMerges) {
            for (const source of merge.sources) {
                if (source !== merge.target) out[source] = merge.target;
            }
        }
        // Der noch nicht vorgemerkte Entwurf zaehlt genauso — sonst steht der
        // Nutzer mit vier angehakten Firmen vor einem grauen Knopf. Seine Quellen
        // sind nie schon anderweitig verplant: toggleDraftSource laesst gesperrte
        // Namen gar nicht erst in den Entwurf.
        if (canAddDraft) {
            for (const source of draftSources) {
                if (source !== draftTarget) out[source] = draftTarget;
            }
        }
        return out;
    }, [groupPlans, manualMerges, canAddDraft, draftSources, draftTarget]);

    // Vorschau aus GENAU derselben Aufloesung wie das Anwenden: ein Zyklus
    // {A:B, B:A} sieht roh nach zwei Umbenennungen aus, wird beim Anwenden aber
    // komplett verworfen. Frueher blieb der Knopf dafuer aktiv.
    const summary = useMemo(() => summarizeMerge(items, mapping), [items, mapping]);
    const { affected, sources: sourceCount, targets: targetCount } = summary;
    const hasRawPlan = Object.keys(mapping).length > 0;
    const canSubmit = affected > 0 && understood && !busy;

    const submit = () => {
        if (!canSubmit) return;
        setBusy(true);
        const result = applyCompanyMerge(items, mapping);
        // Nur die wirklich geaenderten Maengel — unveraenderte kommen als gleiche
        // Referenz zurueck. Daraus die Ziel-Schreibweisen fuer die Rueckmeldung.
        const targets: string[] = [];
        result.items.forEach((next, index) => {
            if (!next || next === items[index]) return;
            const name = next.responsiblePerson;
            if (name && !targets.includes(name)) targets.push(name);
        });
        onApply(result.items, result.changed, targets);
    };

    // Schliessen wirft die gesammelte Handarbeit weg — vorher fragen.
    const hasPendingWork = manualMerges.length > 0 || draftSources.length > 0;
    const requestClose = () => {
        if (
            hasPendingWork &&
            !window.confirm(
                'Die vorgemerkten Zusammenführungen gehen verloren. Fenster trotzdem schließen?',
            )
        ) {
            return;
        }
        onClose();
    };

    // Escape schliesst — ueber eine Referenz, damit der Listener nicht bei jedem
    // Tastendruck neu registriert werden muss.
    const closeRef = useRef(requestClose);
    useEffect(() => {
        closeRef.current = requestClose;
    });
    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') closeRef.current();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    // Anfangsfokus im Dialog: sonst haengt der Fokus in der Auswertung dahinter.
    const dialogRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        dialogRef.current?.focus();
    }, []);

    const suggestionBadge = groups.length > 0 ? ` (${groups.length})` : '';

    return (
        <div className="fixed inset-0 z-[130] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
            <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={TITLE_ID}
                tabIndex={-1}
                className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden animate-scale-in outline-none"
            >
                <div className="bg-slate-900 text-white p-5 flex justify-between items-center shrink-0">
                    <div>
                        <h3 id={TITLE_ID} className="font-bold text-lg flex items-center gap-2">
                            <GitMerge className="w-5 h-5 text-blue-400" /> Firmen zusammenführen
                        </h3>
                        <p className="text-xs text-slate-300 mt-1">
                            Gleiche Firma, verschiedene Schreibweisen — hier laufen sie unter einem Namen zusammen.
                        </p>
                    </div>
                    <button onClick={requestClose} className="hover:bg-white/10 p-1 rounded" title="Schließen">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Zwei gleichrangige Wege — der manuelle ist kein Anhängsel. */}
                <div className="flex gap-1 px-5 pt-4 shrink-0 border-b border-slate-100">
                    <button
                        type="button"
                        onClick={() => setTab('suggestions')}
                        className={`px-4 py-2 text-sm font-bold rounded-t-lg border-b-2 transition-colors ${
                            tab === 'suggestions'
                                ? 'border-blue-600 text-blue-700'
                                : 'border-transparent text-slate-500 hover:text-slate-700'
                        }`}
                    >
                        Vorschläge{suggestionBadge}
                    </button>
                    <button
                        type="button"
                        onClick={() => setTab('manual')}
                        className={`px-4 py-2 text-sm font-bold rounded-t-lg border-b-2 transition-colors ${
                            tab === 'manual'
                                ? 'border-blue-600 text-blue-700'
                                : 'border-transparent text-slate-500 hover:text-slate-700'
                        }`}
                    >
                        Selbst zusammenführen
                        {manualMerges.length > 0 && (
                            <span className="ml-2 bg-blue-100 text-blue-700 text-[11px] px-1.5 py-0.5 rounded-full">
                                {manualMerges.length}
                            </span>
                        )}
                    </button>
                </div>

                <datalist id={DATALIST_ID}>
                    {companySuggestions.map((c) => (
                        <option key={c} value={c} />
                    ))}
                </datalist>

                <div className="p-5 space-y-4 overflow-y-auto flex-1">
                    {tab === 'suggestions' && (
                        <>
                            {groups.length === 0 ? (
                                <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-600 space-y-2">
                                    <p className="font-bold text-slate-700 flex items-center gap-2">
                                        <Sparkles className="w-4 h-4 text-slate-400" /> Keine automatischen Vorschläge
                                    </p>
                                    <p>
                                        Die Ähnlichkeitssuche findet bewusst nur eindeutige Schreibvarianten. Fälle wie
                                        „Meier &amp; Söhne" und „Meier und Söhne" führen Sie unter{' '}
                                        <strong>Selbst zusammenführen</strong> von Hand zusammen.
                                    </p>
                                    <Button variant="secondary" size="sm" onClick={() => setTab('manual')}>
                                        Zur manuellen Auswahl
                                    </Button>
                                </div>
                            ) : (
                                groups.map((group, index) => {
                                    const choice = choices[group.id];
                                    if (!choice) return null;
                                    const target = effectiveTarget(choice);
                                    const warnings = groupWarnings(group);
                                    const sicher = group.confidence === 'sicher' && warnings.length === 0;
                                    const plan = groupPlans.get(group.id);
                                    return (
                                        <div
                                            key={group.id}
                                            className={`rounded-2xl border p-4 transition-colors ${
                                                warnings.length > 0
                                                    ? 'border-orange-300 bg-orange-50/60'
                                                    : choice.enabled
                                                      ? 'border-blue-200 bg-blue-50/40'
                                                      : 'border-slate-200 bg-white'
                                            }`}
                                        >
                                            <div className="flex items-start justify-between gap-3 mb-3">
                                                <label className="flex items-start gap-2 cursor-pointer select-none min-w-0">
                                                    <input
                                                        type="checkbox"
                                                        checked={choice.enabled}
                                                        onChange={(e) => updateChoice(group.id, { enabled: e.target.checked })}
                                                        className="mt-1 w-4 h-4 rounded border-slate-300 text-blue-600 shrink-0"
                                                    />
                                                    <span className="min-w-0">
                                                        <span className="block font-bold text-slate-800 truncate">
                                                            {group.variants.length} Schreibweisen
                                                        </span>
                                                        <span className="block text-xs text-slate-500">
                                                            zusammenführen zu <strong>{target || '—'}</strong>
                                                        </span>
                                                    </span>
                                                </label>
                                                <span
                                                    className={`shrink-0 text-[11px] font-bold px-2 py-1 rounded-full ${
                                                        warnings.length > 0
                                                            ? 'bg-orange-200 text-orange-900'
                                                            : sicher
                                                              ? 'bg-emerald-100 text-emerald-700'
                                                              : 'bg-amber-100 text-amber-800'
                                                    }`}
                                                >
                                                    {/* Kein „sicher": geprüft ist nur, dass derselbe Name anders
                                                        geschrieben ist. Ob hinter „Müller" und „Müller GmbH" eine
                                                        oder zwei Firmen stecken, weiß nur der Nutzer. */}
                                                    {warnings.length > 0
                                                        ? 'genau prüfen'
                                                        : sicher
                                                          ? 'gleicher Name, andere Schreibweise'
                                                          : 'wahrscheinlich — prüfen'}
                                                </span>
                                            </div>

                                            {warnings.length > 0 && (
                                                <ul className="text-xs text-orange-900 bg-orange-100 border border-orange-300 rounded-lg px-3 py-2 mb-3 space-y-1">
                                                    {warnings.map((warning) => (
                                                        <li key={warning} className="flex items-start gap-2">
                                                            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                                                            <span>{warning}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}

                                            {!sicher && warnings.length === 0 && (
                                                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3 flex items-start gap-2">
                                                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                                                    Die Namen sind nur ähnlich, nicht gleich. Bitte prüfen und einzelne
                                                    Schreibweisen abwählen, wenn sie nicht dazugehören.
                                                </p>
                                            )}

                                            <div className="space-y-1">
                                                {group.variants.map((variant) => {
                                                    // Von Hand verplant: die manuelle Zuordnung gilt, hier bleibt
                                                    // die Schreibweise sichtbar gesperrt statt still überschrieben.
                                                    const lockedByManual = isLockedByHand(variant.name, target);
                                                    const isSelected = choice.selected.includes(variant.name) && !lockedByManual;
                                                    const isTarget =
                                                        choice.targetMode === 'variant' && choice.targetVariant === variant.name;
                                                    return (
                                                        <div
                                                            key={variant.name}
                                                            className={`flex items-center gap-3 px-3 py-2 rounded-xl border text-sm ${
                                                                isSelected ? 'bg-white border-slate-200' : 'bg-slate-50 border-transparent opacity-60'
                                                            }`}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                onChange={() => toggleVariant(group, variant.name)}
                                                                disabled={!choice.enabled || lockedByManual}
                                                                title={
                                                                    lockedByManual
                                                                        ? 'Diese Schreibweise ist bereits von Hand verplant'
                                                                        : 'Gehört zu dieser Firma'
                                                                }
                                                                className="w-4 h-4 rounded border-slate-300 text-blue-600 shrink-0"
                                                            />
                                                            <label className="flex items-center gap-2 cursor-pointer min-w-0 flex-1">
                                                                <input
                                                                    type="radio"
                                                                    name={`merge-target-${index}`}
                                                                    checked={isTarget}
                                                                    disabled={!choice.enabled || !isSelected}
                                                                    onChange={() =>
                                                                        updateChoice(group.id, {
                                                                            targetMode: 'variant',
                                                                            targetVariant: variant.name,
                                                                        })
                                                                    }
                                                                    title="Diese Schreibweise behalten"
                                                                    className="w-4 h-4 border-slate-300 text-blue-600 shrink-0"
                                                                />
                                                                <span className={`truncate ${isTarget ? 'font-bold text-blue-800' : 'text-slate-700'}`}>
                                                                    {variant.name}
                                                                </span>
                                                            </label>
                                                            {lockedByManual ? (
                                                                <span className="text-[11px] text-blue-700 shrink-0 flex items-center gap-1">
                                                                    <Lock className="w-3 h-3" /> von Hand verplant
                                                                </span>
                                                            ) : (
                                                                <span className="text-xs text-slate-500 shrink-0">{usageLabel(variant)}</span>
                                                            )}
                                                        </div>
                                                    );
                                                })}

                                                <div className="flex items-center gap-3 px-3 py-2 rounded-xl border border-dashed border-slate-200 text-sm">
                                                    <span className="w-4 shrink-0" />
                                                    <input
                                                        type="radio"
                                                        name={`merge-target-${index}`}
                                                        checked={choice.targetMode === 'custom'}
                                                        disabled={!choice.enabled}
                                                        onChange={() => updateChoice(group.id, { targetMode: 'custom' })}
                                                        title="Andere Schreibweise verwenden"
                                                        className="w-4 h-4 border-slate-300 text-blue-600 shrink-0"
                                                    />
                                                    <input
                                                        type="text"
                                                        list={DATALIST_ID}
                                                        value={choice.customTarget}
                                                        placeholder="Andere Schreibweise (z. B. aus dem Verteiler)…"
                                                        disabled={!choice.enabled}
                                                        onChange={(e) =>
                                                            updateChoice(group.id, {
                                                                customTarget: e.target.value,
                                                                targetMode: 'custom',
                                                            })
                                                        }
                                                        className="flex-1 min-w-0 px-3 py-1.5 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 disabled:bg-slate-50"
                                                    />
                                                </div>
                                            </div>

                                            {choice.targetMode === 'custom' && isNewSpelling(choice.customTarget) && (
                                                <p className="text-xs text-slate-600 mt-2 flex items-start gap-1.5">
                                                    <Sparkles className="w-3.5 h-3.5 mt-0.5 shrink-0 text-slate-400" />
                                                    Diese Schreibweise gibt es noch nicht — sie wird neu angelegt.
                                                </p>
                                            )}

                                            {choice.enabled && !target.trim() && (
                                                <p className="text-xs text-red-600 mt-2 flex items-center gap-1.5">
                                                    <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                                                    Ohne Ziel-Schreibweise wird diese Gruppe nicht zusammengeführt.
                                                </p>
                                            )}

                                            {plan?.targetBlocked && (
                                                <p className="text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 mt-2 flex items-start gap-2">
                                                    <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                                                    Die Ziel-Schreibweise <strong>{target}</strong> wird selbst von Hand
                                                    zusammengeführt. Diese Gruppe bleibt deshalb unberücksichtigt —
                                                    wählen Sie ein anderes Ziel oder entfernen Sie die manuelle
                                                    Zusammenführung.
                                                </p>
                                            )}

                                            {plan && !plan.targetBlocked && !!target.trim() && plan.sources.length === 0 && (
                                                <p className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 mt-2 flex items-start gap-2">
                                                    <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                                                    Hier bleibt keine Schreibweise übrig, die umbenannt würde.
                                                </p>
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </>
                    )}

                    {tab === 'manual' && (
                        <>
                            {manualMerges.length > 0 && (
                                <div className="space-y-2">
                                    <p className="text-[11px] font-bold text-slate-500 uppercase">Vorgemerkt</p>
                                    {manualMerges.map((merge) => (
                                        <div
                                            key={merge.id}
                                            className="flex items-start gap-3 rounded-xl border border-blue-200 bg-blue-50/50 px-3 py-2 text-sm"
                                        >
                                            <GitMerge className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
                                            <span className="flex-1 min-w-0 text-slate-700">
                                                {merge.sources.join(' + ')} <span className="text-slate-400">→</span>{' '}
                                                <strong>{merge.target}</strong>
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => setManualMerges((prev) => prev.filter((m) => m.id !== merge.id))}
                                                className="p-1 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 shrink-0"
                                                title="Entfernen"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="rounded-2xl border border-slate-200 p-4 space-y-3">
                                <p className="text-sm font-bold text-slate-700">Firmen auswählen</p>
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                    <input
                                        type="text"
                                        value={query}
                                        onChange={(e) => setQuery(e.target.value)}
                                        placeholder="Firma suchen… (findet Mueller auch bei der Suche nach Müller)"
                                        className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                                    />
                                </div>

                                <div className="max-h-80 overflow-y-auto space-y-1 border border-slate-100 rounded-xl p-1">
                                    {visibleUsages.map((usage) => {
                                        // Gesperrte Firmen werden gezeigt, nicht versteckt: der Nutzer muss
                                        // sehen können, welches Ziel für eine Firma bereits gilt.
                                        const lockReason = manualLockReason(usage.name);
                                        const isSelected = draftSources.includes(usage.name);
                                        return (
                                            <label
                                                key={usage.name}
                                                className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                                                    lockReason
                                                        ? 'opacity-60 cursor-not-allowed'
                                                        : isSelected
                                                          ? 'bg-blue-50 cursor-pointer'
                                                          : 'hover:bg-slate-50 cursor-pointer'
                                                }`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected && !lockReason}
                                                    disabled={!!lockReason}
                                                    onChange={() => toggleDraftSource(usage.name)}
                                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 shrink-0"
                                                />
                                                <Building2 className="w-4 h-4 text-slate-400 shrink-0" />
                                                <span className="truncate flex-1 text-slate-700">{usage.name}</span>
                                                {lockReason ? (
                                                    <span className="text-[11px] text-blue-700 shrink-0 flex items-center gap-1 max-w-[45%] truncate">
                                                        <Lock className="w-3 h-3 shrink-0" />
                                                        <span className="truncate">{lockReason}</span>
                                                    </span>
                                                ) : (
                                                    <span className="text-xs text-slate-500 shrink-0">{usageLabel(usage)}</span>
                                                )}
                                            </label>
                                        );
                                    })}
                                    {visibleUsages.length === 0 && (
                                        <p className="px-3 py-4 text-center text-xs text-slate-400">
                                            {usages.length === 0
                                                ? 'In den Mängeln ist noch keine Firma eingetragen.'
                                                : 'Keine Firma gefunden.'}
                                        </p>
                                    )}
                                </div>

                                {draftSources.length > 0 && (
                                    <div className="space-y-2 border-t border-slate-100 pt-3">
                                        <p className="text-[11px] font-bold text-slate-500 uppercase">Ziel-Schreibweise</p>
                                        {draftSources.map((name) => (
                                            <label key={name} className="flex items-center gap-2 text-sm cursor-pointer">
                                                <input
                                                    type="radio"
                                                    name="manual-target"
                                                    checked={draftMode === 'variant' && draftVariant === name}
                                                    onChange={() => {
                                                        setDraftMode('variant');
                                                        setDraftVariant(name);
                                                    }}
                                                    className="w-4 h-4 border-slate-300 text-blue-600 shrink-0"
                                                />
                                                <span className="truncate text-slate-700">{name}</span>
                                            </label>
                                        ))}
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="radio"
                                                name="manual-target"
                                                checked={draftMode === 'custom'}
                                                onChange={() => setDraftMode('custom')}
                                                className="w-4 h-4 border-slate-300 text-blue-600 shrink-0"
                                            />
                                            <input
                                                type="text"
                                                list={DATALIST_ID}
                                                value={draftCustom}
                                                placeholder="Andere Schreibweise (z. B. aus dem Verteiler)…"
                                                onChange={(e) => {
                                                    setDraftCustom(e.target.value);
                                                    setDraftMode('custom');
                                                }}
                                                className="flex-1 min-w-0 px-3 py-1.5 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                                            />
                                        </div>
                                        {draftMode === 'custom' && isNewSpelling(draftCustom) && (
                                            <p className="text-xs text-slate-600 flex items-start gap-1.5">
                                                <Sparkles className="w-3.5 h-3.5 mt-0.5 shrink-0 text-slate-400" />
                                                Diese Schreibweise gibt es noch nicht — sie wird neu angelegt.
                                            </p>
                                        )}
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            onClick={addDraft}
                                            disabled={!canAddDraft}
                                            icon={<Plus className="w-4 h-4" />}
                                        >
                                            Vormerken und weitere zusammenstellen
                                        </Button>
                                        {draftConflictHint ? (
                                            <p className="text-xs text-red-600 flex items-start gap-1.5">
                                                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                                                {draftConflictHint}
                                            </p>
                                        ) : (
                                            <p className="text-xs text-slate-500">
                                                {canAddDraft
                                                    ? 'Diese Auswahl steht unten bereits in der Vorschau. Vormerken nur, wenn Sie danach noch eine weitere Zusammenführung zusammenstellen wollen.'
                                                    : 'Eine Ziel-Schreibweise bestimmen, die sich von der Auswahl unterscheidet.'}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>

                {/* Vorschau — gilt fuer beide Wege gemeinsam. */}
                <div className="border-t border-slate-100 bg-slate-50 px-5 py-4 shrink-0 space-y-3">
                    {affected === 0 ? (
                        <p className="text-sm text-slate-500">
                            {hasRawPlan
                                ? 'Die aktuelle Auswahl ändert nichts: die Zuordnungen zeigen im Kreis aufeinander. Bitte eine Ziel-Schreibweise wählen, die nicht selbst umbenannt wird.'
                                : draftSources.length > 0
                                  ? draftConflictHint ??
                                    `${draftSources.length === 1 ? 'Eine Firma' : `${draftSources.length} Firmen`} ausgewählt. Es fehlt noch eine Ziel-Schreibweise, die sich von der Auswahl unterscheidet.`
                                  : 'Noch nichts ausgewählt. Wählen Sie einen Vorschlag aus oder stellen Sie eine Zusammenführung selbst zusammen.'}
                        </p>
                    ) : (
                        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 space-y-2">
                            <p className="font-bold flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4 shrink-0" />
                                Bei {affected} {affected === 1 ? 'Mangel' : 'Mängeln'} wird der Firmenname geändert —{' '}
                                {sourceCount} {sourceCount === 1 ? 'Schreibweise wird' : 'Schreibweisen werden'} zu{' '}
                                {targetCount} {targetCount === 1 ? 'Firma' : 'Firmen'} zusammengeführt.
                            </p>
                            <p className="text-xs">
                                Bereits versendete und archivierte Berichte behalten ihre damalige Schreibweise — sie
                                müssen zu dem passen, was beim Kunden liegt.
                            </p>
                            {/* Der Entwurf zaehlt hier nicht mit — sonst fragt sich der Nutzer, warum
                              * die angehakte Firma in der Zahl oben fehlt. */}
                            {draftConflictHint && (
                                <p className="text-xs text-red-700 flex items-start gap-1.5">
                                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                                    {draftConflictHint}
                                </p>
                            )}
                            <label className="flex items-start gap-2 cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    checked={understood}
                                    onChange={(e) => setUnderstood(e.target.checked)}
                                    className="mt-0.5 w-4 h-4 rounded border-amber-300 text-amber-600 shrink-0"
                                />
                                <span>
                                    Ich habe verstanden, dass die alten Schreibweisen danach nicht mehr in den Mängeln
                                    stehen und sich das nicht zurücknehmen lässt.
                                </span>
                            </label>
                        </div>
                    )}

                    <div className="flex justify-end gap-3">
                        <Button variant="ghost" onClick={requestClose} disabled={busy}>
                            Abbrechen
                        </Button>
                        <Button
                            variant="primary"
                            onClick={submit}
                            disabled={!canSubmit}
                            icon={<GitMerge className="w-4 h-4" />}
                        >
                            Zusammenführen
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};
